import { useState } from "react";
import { wabaApi } from "../../lib/wabaApi";
import { EmbeddedSignup } from "./EmbeddedSignup";
import { AlertTriangleIcon } from "../shared/Icons";

/**
 * Bloco da CONEXÃO EXTERNA no card: sem Meta, sem toggles de envio. O sistema
 * do cliente entrega a mensagem em POST /inbound e recebe a resposta final do
 * agente (lead + mensagem) no webhook de saída cadastrado aqui.
 */
const WABA_URL = "https://waba.revendai.com";
const DOCS_URL = "https://carmillion.mintlify.site/conexao-externa";

const label: React.CSSProperties = { fontSize: 11, color: "var(--ink-muted)", marginBottom: 4 };
const code: React.CSSProperties = {
  fontSize: 12, fontFamily: "monospace", background: "var(--surface-input)", border: "1px solid var(--line)",
  borderRadius: 6, padding: "6px 10px", color: "var(--ink)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const btn: React.CSSProperties = {
  background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 5,
  color: "var(--ink-secondary)", fontSize: 11, padding: "3px 8px", cursor: "pointer", whiteSpace: "nowrap",
};

function Copy({ value }: { value: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button style={btn} onClick={() => { navigator.clipboard.writeText(value); setOk(true); setTimeout(() => setOk(false), 1200); }}>
      {ok ? "Copiado" : "Copiar"}
    </button>
  );
}

export function ExternalConnection({ conn, onChanged }: {
  conn: { id: string; instance_id: string; external_webhook_url?: string | null };
  onChanged: () => void;
}) {
  const [url, setUrl] = useState(conn.external_webhook_url ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [migrar, setMigrar] = useState(false);
  const dirty = url.trim() !== (conn.external_webhook_url ?? "");

  async function save() {
    setSaving(true); setError(null);
    try {
      await wabaApi.patch(`/connections/${conn.id}`, { external_webhook_url: url.trim() });
      await onChanged();
    } catch (e: any) {
      setError(e?.payload?.detail ?? "Não foi possível salvar.");
    } finally { setSaving(false); }
  }

  async function reveal() {
    if (secret) { setSecret(null); return; }
    const d = await wabaApi.get<{ secret: string }>(`/connections/${conn.id}/external-secret`);
    setSecret(d.secret);
  }

  return (
    <div style={{ borderTop: "1px solid var(--line)", paddingTop: 14, marginBottom: 14, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ border: "1px solid var(--line)", borderRadius: 10, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>Conexão externa</div>
          <div style={{ fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.5, marginTop: 4 }}>
            Sem WhatsApp oficial. O seu sistema (ex.: n8n) entrega cada mensagem do cliente em <code>POST /inbound</code> e
            recebe a resposta final do agente no webhook abaixo, com a entidade do lead. Janela de 24h, digitação e
            formatação de envio ficam por conta do sistema de lá.{" "}
            <a href={DOCS_URL} target="_blank" rel="noreferrer" style={{ color: "var(--brand)" }}>Guia completo</a>
          </div>
        </div>

        <div>
          <div style={label}>Entrada — onde o seu sistema envia as mensagens do cliente</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <code style={code}>POST {WABA_URL}/inbound</code>
            <Copy value={`${WABA_URL}/inbound`} />
          </div>
          <div style={{ fontSize: 11, color: "var(--ink-muted)", marginTop: 4 }}>
            Headers <code>x-instance-id</code> e <code>x-api-key</code> desta conexão (acima). Corpo: envelope v2.
          </div>
        </div>

        <div>
          <div style={label}>Webhook de saída — onde a resposta do agente chega</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://seu-n8n.com/webhook/valquiria"
              style={{ ...code, outline: "none" }}
            />
            <button style={{ ...btn, opacity: dirty && !saving ? 1 : 0.5 }} disabled={!dirty || saving} onClick={save}>
              {saving ? "Salvando…" : "Salvar"}
            </button>
          </div>
          {error && <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 4 }}>{error}</div>}
          {!conn.external_webhook_url && !error && (
            <div style={{ fontSize: 11, color: "var(--warn)", marginTop: 4, display: "flex", gap: 6, alignItems: "center" }}>
              <AlertTriangleIcon size={12} /> Sem webhook cadastrado o agente não consegue responder.
            </div>
          )}
        </div>

        <div>
          <div style={label}>Segredo do webhook — chega no header <code>x-webhook-secret</code> de cada POST</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <code style={{ ...code, letterSpacing: secret ? "normal" : "0.1em" }}>{secret ?? "••••••••••••••••••••••••"}</code>
            <button style={btn} onClick={reveal}>{secret ? "Ocultar" : "Revelar"}</button>
            {secret && <Copy value={secret} />}
          </div>
        </div>
      </div>

      <div style={{ border: "1px solid var(--line)", borderRadius: 10, background: "var(--surface-input)" }}>
        <button
          onClick={() => setMigrar(m => !m)}
          aria-expanded={migrar}
          style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
        >
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>Migrar para WhatsApp oficial</span>
          <span aria-hidden style={{ marginLeft: "auto", fontSize: 10, color: "var(--ink-muted)" }}>{migrar ? "▾" : "▸"}</span>
        </button>
        {migrar && (
          <div style={{ padding: "0 14px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "var(--warn-soft)", border: "1px solid var(--warn-line)", borderRadius: 8, padding: "8px 12px" }}>
              <span style={{ display: "inline-flex", lineHeight: 1, color: "var(--warn)", marginTop: 1 }}><AlertTriangleIcon size={12} /></span>
              <span style={{ fontSize: 12, color: "var(--warn)", lineHeight: 1.5 }}>
                Ao vincular um número, esta conexão deixa de ser externa: o webhook de saída <strong>para de receber</strong> as
                respostas e o agente passa a responder pela Meta. O <code>/inbound</code> deixa de aceitar mensagens.
                Leads, conversas e o agente vinculado são preservados.
              </span>
            </div>
            <EmbeddedSignup connectionId={conn.id} onSuccess={onChanged} />
          </div>
        )}
      </div>
    </div>
  );
}
